import React, { useEffect, useState, useRef } from "react";
import secureAxios from "../../../utils/secureAxios";
import { useAuth } from "../../../contexts/AuthContext";
import WidgetCard, {
  WidgetSuccessState,
  WidgetProgressBar,
} from "./WidgetCard";

const AuthUsageWarningWidget = ({ onlyMyData }) => {
  const { user } = useAuth();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  const hasFetchedRef = useRef(false);
  const lastParamsRef = useRef(null);

  useEffect(() => {
    const fetchWarnings = async () => {
      if (!user?.user_id || !user?.tenant_id) {
        setLoading(false);
        return;
      }

      const paramsKey = `${user.user_id}-${user.tenant_id}-${onlyMyData}`;

      if (hasFetchedRef.current && lastParamsRef.current === paramsKey) {
        return;
      }

      hasFetchedRef.current = true;
      lastParamsRef.current = paramsKey;
      setLoading(true);

      try {
        const response = await secureAxios.get("/api/widgets/auth-warnings", {
          params: {
            only_my_data: onlyMyData,
          },
        });
        setData(response.data || []);
      } catch (error) {
        if (process.env.NODE_ENV === "development") {
          console.error("Error fetching authorization warnings", error);
        }
        setData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchWarnings();
  }, [user?.user_id, user?.tenant_id, onlyMyData]);

  // Show success state when no warnings
  if (!loading && data.length === 0) {
    return (
      <WidgetCard
        title="Authorization Usage"
        icon="bi-exclamation-triangle"
        iconColor="success"
        loading={loading}
      >
        <WidgetSuccessState message="No authorizations near their limit" />
      </WidgetCard>
    );
  }

  return (
    <WidgetCard
      title="Authorization Usage Warning"
      icon="bi-exclamation-triangle"
      iconColor="danger"
      loading={loading}
      badge={data.length > 0 ? data.length : undefined}
      badgeColor="danger"
    >
      {/* Desktop table view */}
      <div className="d-none d-md-block">
        <div className="table-responsive">
          <table className="table table-sm widget-table align-middle">
            <thead className="table-light">
              <tr>
                <th>Auth #</th>
                <th>Consumer</th>
                <th>Type</th>
                <th className="text-end">Total</th>
                <th className="text-end">Used</th>
                <th className="text-end">% Left</th>
              </tr>
            </thead>
            <tbody>
              {data.map((auth) => {
                const used =
                  auth.auth_type === "mileage"
                    ? parseFloat(auth.total_miles_used || 0).toFixed(1)
                    : parseFloat(auth.total_hours_used || 0).toFixed(1);

                const percentRemaining = parseFloat(
                  auth.percent_remaining || 0
                );
                const percentUsed = 100 - Math.max(percentRemaining, 0);
                const progressWidth = Math.min(Math.max(percentUsed, 0), 100);
                const statusClass = percentRemaining < 0 ? "danger" : "warning";

                return (
                  <tr key={auth.authorization_id}>
                    <td className="widget-auth-number">
                      {auth.authorization_number}
                    </td>
                    <td className="small">{auth.consumer_name}</td>
                    <td>
                      <span className={`badge bg-${statusClass}`}>
                        {auth.auth_type}
                      </span>
                    </td>
                    <td className="small text-end">
                      {parseFloat(auth.authorization_hours || 0).toFixed(1)}
                    </td>
                    <td className="small text-end">{used}</td>
                    <td className="text-end">
                      <div className="d-flex align-items-center justify-content-end">
                        <span
                          className={`small fw-bold text-${statusClass} me-2`}
                        >
                          {percentRemaining.toFixed(1)}%
                        </span>
                        <div style={{ width: "40px" }}>
                          <WidgetProgressBar
                            segments={[
                              { color: statusClass, percent: progressWidth },
                            ]}
                          />
                        </div>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobile view */}
      <div className="d-block d-md-none">
        {data.map((auth) => {
          const used =
            auth.auth_type === "mileage"
              ? parseFloat(auth.total_miles_used || 0).toFixed(1)
              : parseFloat(auth.total_hours_used || 0).toFixed(1);

          const total = parseFloat(auth.authorization_hours || 0).toFixed(1);
          const percentRemaining = parseFloat(auth.percent_remaining || 0);
          const statusClass = percentRemaining < 0 ? "danger" : "warning";

          return (
            <div
              key={auth.authorization_id}
              className={`widget-mobile-card widget-mobile-card--${statusClass} border`}
            >
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <span className="widget-auth-number">
                    {auth.authorization_number}
                  </span>
                  <span className={`badge bg-${statusClass} ms-2 small`}>
                    {auth.auth_type}
                  </span>
                  <div className="small text-muted">{auth.consumer_name}</div>
                </div>
                <div className="text-end">
                  <div className={`small fw-bold text-${statusClass}`}>
                    {percentRemaining.toFixed(1)}%
                  </div>
                  <div className="small text-muted">
                    {used}/{total}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </WidgetCard>
  );
};

export default AuthUsageWarningWidget;
